import { useCallback, useEffect, useState } from 'react';
import { departmentsAPI, locationsAPI } from '../services/api';
import { MapPin, Pencil, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const emptyLocationForm = {
  name: '',
  description: '',
  department_id: '',
};

const Locations = () => {
  const { hasPermission } = useAuth();
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyLocationForm);

  const loadData = useCallback(async () => {
    const [locationsRes, departmentsRes] = await Promise.all([
      locationsAPI.getAll(),
      departmentsAPI.getAll(),
    ]);

    setLocations(locationsRes.data.locations);
    setDepartments(departmentsRes.data.departments);
  }, []);

  useEffect(() => {
    loadData().catch(() => toast.error('Failed to load locations'));
  }, [loadData]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);
      const payload = {
        ...form,
        department_id: form.department_id || null,
      };
      if (editingId) {
        await locationsAPI.update(editingId, payload);
        toast.success('Location updated successfully');
      } else {
        await locationsAPI.create(payload);
        toast.success('Location created successfully');
      }
      setEditingId(null);
      setForm(emptyLocationForm);
      setShowForm(false);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save location');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (location) => {
    setEditingId(location.id);
    setForm({
      name: location.name || '',
      description: location.description || '',
      department_id: location.department_id || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (locationId) => {
    if (!window.confirm('Delete this location? Readers and timetable entries may be detached automatically.')) {
      return;
    }

    try {
      await locationsAPI.remove(locationId);
      toast.success('Location deleted successfully');
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete location');
    }
  };

  const canManage = hasPermission('locations.manage');

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Locations</h1>
          <p className="text-gray-500 text-sm mt-1">Manage classrooms, labs, gates, and other physical attendance points.</p>
        </div>
        {canManage && (
          <button
            onClick={() => {
              setEditingId(null);
              setForm(emptyLocationForm);
              setShowForm((prev) => !prev);
            }}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            <Plus size={16} />
            {showForm ? 'Close Form' : 'Add Location'}
          </button>
        )}
      </div>

      {showForm && canManage && (
        <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Location Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Room 101"
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Department</label>
            <select
              value={form.department_id}
              onChange={(e) => setForm({ ...form, department_id: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">No department</option>
              {departments.map((department) => (
                <option key={department.id} value={department.id}>{department.name}</option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="First floor classroom"
            />
          </div>

          <div className="md:col-span-2 flex gap-3">
            <button type="submit" disabled={saving} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60">
              {saving ? 'Saving...' : editingId ? 'Update Location' : 'Create Location'}
            </button>
            <button type="button" onClick={() => { setShowForm(false); setEditingId(null); setForm(emptyLocationForm); }} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        {locations.length === 0 ? (
          <div className="p-8 text-center text-gray-400">No locations found</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
              <tr>
                <th className="px-4 py-3 text-left">Location</th>
                <th className="px-4 py-3 text-left">Department</th>
                <th className="px-4 py-3 text-left">Description</th>
                <th className="px-4 py-3 text-left">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {locations.map((location) => (
                <tr key={location.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">
                    <div className="flex items-center gap-2">
                      <MapPin size={16} className="text-blue-600" />
                      {location.name}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{location.department_name || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{location.description || '—'}</td>
                  <td className="px-4 py-3">
                    {canManage ? (
                      <div className="flex gap-2">
                        <button onClick={() => startEdit(location)} className="inline-flex items-center gap-1 rounded-lg border border-blue-200 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50">
                          <Pencil size={12} />
                          Edit
                        </button>
                        <button onClick={() => handleDelete(location.id)} className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50">
                          <Trash2 size={12} />
                          Delete
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">View only</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Locations;
